import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ProfilerNavbar from './profilerNavBar';
import { FiEdit, FiCheckCircle, FiDownload, FiTrash2, FiPlus, FiX, FiEye, FiChevronDown } from 'react-icons/fi';
import { mockDropdownOptions } from '../../../data/mockDropdownOptions';
import SkillBadge from '../../../common/skillBadge';
import RadialProgressAvatar from '../jobsRecommendationPage/radialProgressAvatar';
import { useNavigate } from 'react-router-dom';
import { resumeUpload } from '../../../supports/resumeUpload';
import MultiSelectCandidateDropdown from '../../../common/multiSelectCandidate';
import RenderTags from './renderTags';
import { useToast } from '../../../common/toastSystem';
import LanguageSelector from './languageSelector';
import { generateRequestId } from '../../../common/common';
import EditModal from '../../../common/modal';
import { RecruiterApi } from '../../../supports/recruiterApi';
import InfoItem from './infoItem';
import SectionCard from './sectionCard';
import { resolveToOptionObj, resolveToOptionObjArray, parseEducationString, deepClone } from './utils';
import useDropdownData from './useDropDownData';
import ResumeModal from '../../../common/resumeModal';

// ─────────────────────────────────────────────────────────────
// HELPERS (module-level, never recreated)
// ─────────────────────────────────────────────────────────────
function extractLabels(value) {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(val => typeof val === 'object' ? (val.label ?? val.name ?? val.value ?? val.id) : val);
  if (typeof value === 'object') return value.label ?? value.name ?? value.value ?? value.id ?? '';
  return value;
}

function parsePreferredLocationValues(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') return value.split(',').map(v => v.trim()).filter(Boolean);
  if (typeof value === 'object') return [value];
  return [];
}

function isHeaderSectionValid(data) {
  if (!data.first_name || !data.last_name) return false;
  if (!data.location || data.location.id === 'Nil') return false;
  if (!data.phone || !data.gender) return false;
  if (!data.languages || !Array.isArray(data.languages) || data.languages.length === 0) return false;

  // Nationality: valid if disclosed OR has a value
  const nationalityFilled =
    data.nationality_disclosed === true ||
    (data.nationality != null && data.nationality !== '');
  if (!nationalityFilled) return false;

  // Date of Birth: valid if disclosed OR has a valid non-future date
  if (!data.dob_disclosed) {
    if (!data.date_of_birth) return false;  // empty = invalid
    // Guard against future dates reaching here (belt-and-suspenders)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dob = new Date(data.date_of_birth);
    if (isNaN(dob.getTime()) || dob > today) return false;
  }

  return true;
}

function isSectorInfoSectionValid(data) {
  if (!data.sector_name || !Array.isArray(data.sector_name) || data.sector_name.length === 0) return false;
  if (!data.project_phase || !Array.isArray(data.project_phase) || data.project_phase.length === 0) return false;
  return true;
}

function isCareerSectionValid() { return true; }

function isSkillsSectionValid(data) {
  return data.skills && Array.isArray(data.skills) && data.skills.length > 0;
}

function isEducationSectionValid(data) {
  if (!data.education_details || !Array.isArray(data.education_details) || data.education_details.length === 0) return false;
  for (const edu of data.education_details) { if (!edu.degree || !edu.institution) return false; }
  return true;
}

function isExperienceSectionValid(data) {
  if (!data.experience_intervals || !Array.isArray(data.experience_intervals) || data.experience_intervals.length === 0) return false;
  for (const exp of data.experience_intervals) { if (!exp.title || !exp.company) return false; }
  return true;
}

function missingFieldsMessage(sectionKey) {
  switch (sectionKey) {
    case 'header': return 'Please fill all required personal information fields: First Name, Last Name, Location, Phone number, Gender, Languages.';
    case 'sectorInfo': return 'Please fill all required sector information fields: Sector and Project Phases.';
    case 'career': return 'Please fill all required career preference fields: Preferred Location, Availability to work.';
    case 'skills': return 'Please add at least one skill.';
    case 'education': return 'Please provide at least one education entry with Degree and Institution.';
    case 'experience': return 'Please provide at least one experience entry with Title and Company.';
    default: return 'Please fill all required fields.';
  }
}

function validateSectionData(editingSection, data) {
  switch (editingSection) {
    case 'header': return isHeaderSectionValid(data);
    case 'sectorInfo': return isSectorInfoSectionValid(data);
    case 'career': return isCareerSectionValid(data);
    case 'skills': return isSkillsSectionValid(data);
    case 'education': return isEducationSectionValid(data);
    case 'experience': return isExperienceSectionValid(data);
    default: return true;
  }
}

// ─────────────────────────────────────────────────────────────
// CollapsibleTags — outside ProfilerPage so it's never recreated
// ─────────────────────────────────────────────────────────────
const CollapsibleTags = React.memo(({ values, limit = 1 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!values || values.length === 0) return <span className="text-xs text-gray-400 italic">Nil</span>;
  const items = Array.isArray(values) ? values : [values];
  const visible = expanded ? items : items.slice(0, limit);
  const overflow = items.length - limit;
  return (
    <div className="flex flex-wrap gap-1 items-center">
      {visible.map((item, idx) => (
        <span key={idx} className="inline-flex items-center px-2 py-0.5 mph-light mph-primary text-[0.6rem] sm:text-xs font-semibold px-2 py-1 rounded-full"
          style={{ display: 'inline-block', verticalAlign: 'bottom', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {typeof item === 'object' ? (item.label ?? item.name ?? item.value ?? '') : item}
        </span>
      ))}
      {!expanded && overflow > 0 && (
        <button type="button" onClick={() => setExpanded(true)}
          className="inline-text-xs px-2 py-1 rounded-full mph-primary mph-light flex items-center gap-1 text-[11px] font-semibold transition-colors">
          +{overflow}<FiChevronDown size={14} />
        </button>
      )}
      {expanded && items.length > limit && (
        <button type="button" onClick={() => setExpanded(false)}
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors">
          Show less
        </button>
      )}
    </div>
  );
});

// ─────────────────────────────────────────────────────────────
// ExperienceSection — outside ProfilerPage
// ─────────────────────────────────────────────────────────────
const ExperienceSection = React.memo(({
  profile, editingSection, modalTempData, setModalTempData,
  handleEdit, handleModalClose, handleModalSave, handleModalRemoveArrayItem,
  loadingSection, openSections, setOpenSections, isModalSaving, hasChanges, toast
}) => {
  const newExpRef = useRef(null);
  const listRef = useRef(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const experiencesWithId = (profile.experience_intervals || []).map((exp) => ({
    ...exp,
    id: exp.id || `${exp.title}-${exp.company}-${exp.startDate || exp.start_date}-${exp.endDate || exp.end_date}`
  }));

  const formatDateDisplay = (dateString) => {
    if (!dateString) return 'Present';
    const todayStr = new Date().toISOString().split('T')[0];
    if (dateString === todayStr) return 'Present';
    let normalizedDate = dateString;
    if (/^\d{4}$/.test(dateString)) normalizedDate = `${dateString}-01-01`;
    else if (/^\d{4}-\d{2}$/.test(dateString)) normalizedDate = `${dateString}-01`;
    const date = new Date(normalizedDate);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getFormattedRange = (exp) => {
    const startVal = exp.startDate || exp.start_date;
    const endVal = exp.endDate || exp.end_date;
    const showStart = formatDateDisplay(startVal);
    const showEnd = endVal ? formatDateDisplay(endVal) : 'Present';
    return showStart || showEnd ? `${showStart} - ${showEnd}` : '';
  };

  const handleModalAddExperienceTop = useCallback(() => {
    setModalTempData(prev => ({
      ...prev,
      experience_intervals: [
        { id: Date.now(), title: '', company: '', start_date: '', end_date: '', currentlyWorking: false },
        ...(prev.experience_intervals || []),
      ]
    }));
    setTimeout(() => {
      if (newExpRef.current) {
        newExpRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = newExpRef.current.querySelector('input, textarea, select');
        if (input) input.focus();
      }
    }, 30);
  }, [setModalTempData]);

  const normalizeExperienceDateInput = (val) => {
    if (!val) return '';
    if (/^\d{4}$/.test(val)) return `${val}-01-01`;
    if (/^\d{4}-\d{2}$/.test(val)) return `${val}-01`;
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    return val;
  };

  const isExperienceCurrentlyWorking = (exp, todayStr = new Date().toISOString().split('T')[0]) => {
    const explicitCurrent = exp.currentlyWorking === true || exp.currentlyWorking === 'true';
    const explicitNotCurrent = exp.currentlyWorking === false || exp.currentlyWorking === 'false';
    const userTouchedCurrentFlag = exp.currentlyWorkingTouched === true || exp.currentlyWorkingTouched === 'true';
    const normalizedEndDate = normalizeExperienceDateInput(exp.end_date || exp.endDate || '');
    if (explicitCurrent) return true;
    if (explicitNotCurrent && userTouchedCurrentFlag) return false;
    return normalizedEndDate === todayStr;
  };

  // ── Core date validation helper ───────────────────────────────
  const validateExpDate = (val, fieldName, exp) => {
    // Returns error string or '' if valid
    if (!val) return ''; // presence checked separately

    const toLocalDateOnly = (dateVal) => {
      if (!dateVal) return null;
      const asString = String(dateVal);
      const ymdMatch = asString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (ymdMatch) {
        return new Date(Number(ymdMatch[1]), Number(ymdMatch[2]) - 1, Number(ymdMatch[3]));
      }
      const parsedDate = new Date(asString);
      if (isNaN(parsedDate.getTime())) return null;
      return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const parsed = toLocalDateOnly(val);
    if (!parsed || isNaN(parsed.getTime())) return 'Invalid date.';

    const year = parsed.getFullYear();
    if (year < 1950) return 'Year must be 1950 or later.';
    if (year > today.getFullYear() + 1) return `Year seems too far in the future.`;

    if (fieldName === 'start_date') {
      if (parsed > today) return 'Start date cannot be in the future.';
    }

    if (fieldName === 'end_date') {
      const startVal = exp.start_date || exp.startDate;
      if (startVal) {
        const startParsed = toLocalDateOnly(startVal);
        if (startParsed && !isNaN(startParsed.getTime()) && parsed < startParsed) {
          return 'End date cannot be before start date.';
        }
      }
      if (parsed > today) return 'End date cannot be in the future.';
    }

    return '';
  };
  // ─────────────────────────────────────────────────────────────

  const getExperienceValidationErrors = (arr) => (arr || []).map((exp) => {
    const todayStr = new Date().toISOString().split('T')[0];
    const startDate = exp.start_date || exp.startDate;
    const endDate = exp.end_date || exp.endDate;
    const isWorking = isExperienceCurrentlyWorking(exp, todayStr);
    const errors = [];

    if (!exp.title?.trim()) errors.push('Title is required');
    if (!exp.company?.trim()) errors.push('Company is required');

    if (!startDate) {
      errors.push('Start date is required');
    } else {
      const startErr = validateExpDate(startDate, 'start_date', exp);
      if (startErr) errors.push(`Start date: ${startErr}`);
    }

    if (!isWorking) {
      if (!endDate) {
        errors.push('End date is required');
      } else {
        const endErr = validateExpDate(endDate, 'end_date', exp);
        if (endErr) errors.push(`End date: ${endErr}`);
      }
    }

    return errors.join(', ');
  });

  const handleModalArrayChange = useCallback((field, index, key, value) => {
    setModalTempData(prev => {
      const arr = prev[field] || [];
      if (arr[index] && arr[index][key] === value) return prev;
      const newArr = [...arr];
      newArr[index] = { ...newArr[index], [key]: value };
      if (key === 'currentlyWorking') {
        newArr[index].currentlyWorkingTouched = true;
        if (value === true) newArr[index].end_date = null;
      }
      if (key === 'end_date' && value) newArr[index].currentlyWorking = false;
      return { ...prev, [field]: newArr };
    });
  }, [setModalTempData]);

  const checkValidationAndSave = useCallback(() => {
    const experienceArr = modalTempData?.experience_intervals || [];
    const validationErrors = getExperienceValidationErrors(experienceArr);
    if (validationErrors.some(e => e)) { setShowValidationErrors(true); return; }
    setShowValidationErrors(false);
    handleModalSave();
  }, [modalTempData, handleModalSave]);

  return (
    <SectionCard
      title={`Experience${profile.years_of_experience ? ` (${profile.years_of_experience} years)` : ''}`}
      editKey="experience" isRequired={true} onEdit={handleEdit}
      loading={loadingSection === 'experience'} isOpen={openSections['experience']}
      toggleOpen={() => setOpenSections(prev => ({ ...prev, experience: !prev['experience'] }))}
    >
      <div className="space-y-6 scale-[0.9] sm:scale-100">
        {experiencesWithId.map((exp) => (
          <div key={exp.id} className="border-b border-gray-100 pb-6 last:border-b-0">
            <p className="font-semibold text-sm text-gray-333">{exp.title} - {exp.company}</p>
            <p className="text-xs text-gray-999">{getFormattedRange(exp)}</p>
          </div>
        ))}
      </div>

      <EditModal isOpen={editingSection === 'experience'} onClose={handleModalClose}
        onSave={checkValidationAndSave} title="Edit Experience" isSaving={isModalSaving} hasChanges={hasChanges}>
        {modalTempData && (() => {
          const experienceArr = modalTempData.experience_intervals || [];
          const validationErrors = getExperienceValidationErrors(experienceArr);
          const today = new Date().toISOString().split('T')[0];

          return (
            <>
              <div ref={listRef} className="space-y-6" style={{ height: 'calc(100vh - 19rem)', overflowY: 'auto' }}>
                {experienceArr.map((exp, i) => {
                  const isWorking = isExperienceCurrentlyWorking(exp, today);
                  const startVal = normalizeExperienceDateInput(exp.start_date || '');
                  const endVal = normalizeExperienceDateInput(exp.end_date || '');

                  // Per-field inline errors (shown even before form submit attempt)
                  const inlineStartErr = startVal ? validateExpDate(startVal, 'start_date', exp) : '';
                  const inlineEndErr = (!isWorking && endVal) ? validateExpDate(endVal, 'end_date', { ...exp, start_date: startVal }) : '';

                  return (
                    <div key={exp.id || i} ref={i === 0 ? newExpRef : null} className="border-b border-gray-100 pb-6 last:border-b-0">
                      <div className="space-y-3 relative">
                        <button type="button" tabIndex={-1}
                          onClick={e => { e.stopPropagation(); handleModalRemoveArrayItem('experience_intervals', i); }}
                          className="absolute top-0 right-0 text-red-500 hover:text-red-700">
                          <FiTrash2 />
                        </button>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pr-8">
                          <InfoItem label="Title" name="title" isEditing={true} isRequired value={exp.title || ''}
                            onChange={(val) => handleModalArrayChange('experience_intervals', i, 'title', val)} type="text" />
                          <InfoItem label="Company" name="company" isEditing={true} isRequired value={exp.company || ''}
                            onChange={(val) => handleModalArrayChange('experience_intervals', i, 'company', val)} type="text" />

                          {/* ── Start Date with inline error ── */}
                          <div>
                            <label className="block text-xs text-gray-999 mb-1">
                              Start Date <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              value={startVal}
                              max={today}
                              onChange={e => handleModalArrayChange('experience_intervals', i, 'start_date', e.target.value)}
                              className={`font-semibold text-gray-333 text-xs mt-1 p-1 border rounded-md w-3/4 ${
                                inlineStartErr ? 'border-red-400' : 'border-gray-300'
                              }`}
                            />
                            {inlineStartErr && (
                              <p className="text-xs text-red-500 mt-1">{inlineStartErr}</p>
                            )}
                          </div>

                          {/* ── End Date with inline error ── */}
                          <div>
                            <label className="block text-xs text-gray-999 mb-1">End Date</label>
                            <input
                              type="date"
                              value={isWorking ? '' : endVal}
                              min={startVal || undefined}
                              max={today}
                              disabled={isWorking}
                              onChange={e => handleModalArrayChange('experience_intervals', i, 'end_date', e.target.value)}
                              className={`font-semibold text-gray-333 text-xs mt-1 p-1 border rounded-md w-3/4 ${
                                isWorking
                                  ? 'bg-gray-100 cursor-not-allowed opacity-50 border-gray-200'
                                  : inlineEndErr ? 'border-red-400' : 'border-gray-300'
                              }`}
                            />
                            {!isWorking && inlineEndErr && (
                              <p className="text-xs text-red-500 mt-1">{inlineEndErr}</p>
                            )}
                          </div>

                          <InfoItem label="Currently Working" name="currentlyWorking" type="checkbox" isEditing={true} value={isWorking}
                            onChange={(val) => handleModalArrayChange('experience_intervals', i, 'currentlyWorking', val)} />
                        </div>

                        {/* Row-level error summary (shown after Save attempt) */}
                        {showValidationErrors && validationErrors[i] && (
                          <div className="text-xs text-red-500 mt-1 bg-red-50 border border-red-200 rounded px-2 py-1">
                            {validationErrors[i]}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <button type="button" onClick={e => { e.preventDefault(); e.stopPropagation(); handleModalAddExperienceTop(); }}
                className="mph-primary font-semibold text-xs flex items-center gap-1 mt-1 hover:opacity-80"
                style={{ position: 'sticky', bottom: 0, background: '#fff' }}>
                <FiPlus /> Add Experience
              </button>
            </>
          );
        })()}
      </EditModal>
    </SectionCard>
  );
});

// ─────────────────────────────────────────────────────────────
// EducationSection — outside ProfilerPage
// ─────────────────────────────────────────────────────────────
const EducationSection = React.memo(({
  profile, editingSection, modalTempData, setModalTempData,
  handleEdit, handleModalClose, handleModalSave,
  loadingSection, openSections, setOpenSections, isModalSaving, hasChanges,
  toast, showDeclarationModal, declarationChecked, setDeclarationChecked,
  setShowDeclarationModal, setIsReviewClicked, verifyProfile, onBack
}) => {
  const newEduRef = useRef(null);
  const listRef = useRef(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const educationArray = (profile.education_details || []).map((edu) =>
    typeof edu === 'string' ? parseEducationString(edu) : edu
  );

  const getEducationValidationErrors = (arr) => (arr || []).map((edu) => {
    const errors = [];
    if (!edu.degree?.trim()) errors.push('Degree is required');
    if (!edu.institution?.trim()) errors.push('Institution is required');
    const yearOfPassing = edu.duration_text || edu.year_of_completion || '';
    const yearOfStudy = edu.duration_total || edu.year_of_study || '';
    if (!yearOfPassing.trim()) errors.push('Year of passing is required');
    if (!yearOfStudy.trim()) errors.push('Years of study is required');
    return errors.join(', ');
  });

const handleModalArrayChange = useCallback((field, index, key, value, clearKeys = []) => {
  setModalTempData(prev => {
    const arr = prev[field] || [];
    if (arr[index] && arr[index][key] === value) return prev;
    const newArr = [...arr];
    const updated = { ...newArr[index], [key]: value };
    // Clear sibling fallback fields so || chains don't refill the value
    clearKeys.forEach(k => { updated[k] = ''; });
    newArr[index] = updated;
    return { ...prev, [field]: newArr };
  });
}, [setModalTempData]);

  const handleAddEducationTop = useCallback(() => {
    setModalTempData(prev => ({
      ...prev,
      education_details: [
        { id: Date.now(), degree: '', institution: '', startDate: '', endDate: '', field: '', grade: '', details: '' },
        ...(prev.education_details || []),
      ]
    }));
    setTimeout(() => {
      if (newEduRef.current) {
        newEduRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = newEduRef.current.querySelector('input, textarea, select');
        if (input) input.focus();
      }
    }, 30);
  }, [setModalTempData]);

  const handleSave = useCallback(() => {
    const validationErrors = getEducationValidationErrors(modalTempData?.education_details || []);
    if (validationErrors.some(e => e)) {
      setShowValidationErrors(true);
      const uniqueErrors = [...new Set(validationErrors.filter(Boolean))];
      toast.warning(uniqueErrors.join(' | '));
      return;
    }
    if (!isEducationSectionValid(modalTempData)) { toast.warning(missingFieldsMessage('education')); return; }
    setShowValidationErrors(false);
    handleModalSave();
  }, [modalTempData, handleModalSave, toast]);

  return (
    <SectionCard title="Education" editKey="education" isRequired={true} onEdit={handleEdit}
      loading={loadingSection === 'education'} isOpen={openSections['education']}
      toggleOpen={() => setOpenSections(prev => ({ ...prev, education: !prev['education'] }))}>

      <div className="space-y-6 scale-[0.9] sm:scale-100">
        {educationArray.map((edu, i) => (
          <div key={i} className="border-b border-gray-100 pb-6 last:border-b-0">
            <div className="font-semibold text-sm text-gray-333"> {edu.degree} {edu.duration_text && `(${edu.duration_text})`}</div>
            {(edu.field || edu.major || edu.field_of_study) && (
              <div className="text-xs text-gray-600 mt-2">Field: {edu.field || edu.major || edu.field_of_study}</div>
            )}
            {edu.institution && <div className="text-xs text-gray-999 mt-1">{edu.institution}</div>}
            {/* {edu.duration_text && <div className="text-xs text-gray-999 mt-1">{edu.duration_text}</div>} */}
            {edu.grade && <div className="text-xs text-gray-600">Grade: {edu.grade}</div>}
          </div>
        ))}
      </div>

      <EditModal isOpen={editingSection === 'education'} onClose={handleModalClose}
        onSave={handleSave} title="Edit Education" isSaving={isModalSaving} hasChanges={hasChanges}>
        {modalTempData && (() => {
          const educationArr = modalTempData.education_details || [];
          const validationErrors = getEducationValidationErrors(educationArr);
          return (
            <>
              <div ref={listRef} className="space-y-6" style={{ height: 'calc(100vh - 19rem)', overflowY: 'auto' }}>
                {educationArr.map((edu, i) => (
                  <div key={edu.id || i} ref={i === 0 ? newEduRef : null} className="border-b border-gray-100 pb-6 last:border-b-0">
                    <div className="space-y-3 relative">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pr-8">
                        <InfoItem label="Degree" name="degree" value={edu.degree} isEditing isRequired
                          onChange={val => handleModalArrayChange('education_details', i, 'degree', val)} type="text" />
                                              <InfoItem label="Institution" name="institution" value={edu.institution} isEditing isRequired
                                                onChange={val => handleModalArrayChange('education_details', i, 'institution', val)} type="text" />
                                   <InfoItem 
  label="Year of Passing" 
  name="duration_text" 
  value={edu.duration_text ?? edu.year_of_completion ?? ''} 
  isEditing isRequired
  onChange={val => handleModalArrayChange('education_details', i, 'duration_text', val, ['year_of_completion'])} 
  type="text" 
/>
                        {/* <InfoItem label="Years of Study" name="duration_total" value={edu.duration_total || edu.year_of_study || ''} isEditing isRequired
                          onChange={val => handleModalArrayChange('education_details', i, 'duration_total', val)} type="number" inputMode="numeric"
                          pattern="[0-9]*"/> */}
                        <InfoItem
                              label="Years of Study"
                              name="duration_total"
                              value={edu.duration_total || edu.year_of_study || ''}
                              isEditing
                              isRequired
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              onChange={val =>
                                handleModalArrayChange(
                                  'education_details',
                                  i,
                                  'duration_total',
                                  val.replace(/\D/g, '')
                                )
                              }
                            />
                        <InfoItem
                          label="Field of Study"
                          name="field"
                          value={edu.field || edu.major || edu.field_of_study || ''}
                          isEditing
                          onChange={val => handleModalArrayChange('education_details', i, 'field', val, ['field_of_study', 'field_of_study'])}
                          type="text"
                        />

                        <InfoItem label="Grade" name="grade" value={edu.grade || edu.score || ''} isEditing
                          onChange={val => handleModalArrayChange('education_details', i, 'grade', val)} type="text" />
                      </div>
                      {showValidationErrors && validationErrors[i] && (
                        <div className="text-xs text-red-500 mt-1">{validationErrors[i]}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={e => { e.preventDefault(); e.stopPropagation(); handleAddEducationTop(); }}
                className="mph-primary font-semibold text-xs flex items-center gap-1 mt-1 hover:opacity-80"
                style={{ position: 'sticky', bottom: 0, background: '#fff' }}>
                <FiPlus /> Add Education
              </button>
            </>
          );
        })()}
      </EditModal>

      {showDeclarationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-2xl w-full mx-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Declaration</h2>
            <p className="text-sm text-gray-600 mb-4">Before proceeding, please confirm the following declaration:</p>
            <div className="flex items-start gap-2 mb-6">
              <input type="checkbox" id="declarationCheck" checked={declarationChecked}
                onChange={e => setDeclarationChecked(e.target.checked)} className="mt-1 w-4 h-4 cursor-pointer" />
              <label htmlFor="declarationCheck" className="text-sm text-gray-700 cursor-pointer">
                I have reviewed all the information and confirm that the details provided are accurate and complete to the best of my knowledge.
              </label>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowDeclarationModal(false); setDeclarationChecked(false); setIsReviewClicked(true); }}
                className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Review</button>
              <button disabled={!declarationChecked}
                onClick={async () => { if (!declarationChecked) return; await verifyProfile(); setShowDeclarationModal(false); setDeclarationChecked(false); onBack(); }}
                className={`px-4 py-2 text-xs text-white rounded-lg ${declarationChecked ? 'profiler-bg hover:bg-blue-700' : 'profiler-bg opacity-50 cursor-not-allowed'}`}>
                Confirm & Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
});

// ─────────────────────────────────────────────────────────────
// SkillsSection — outside ProfilerPage
// ─────────────────────────────────────────────────────────────
const SkillsSection = React.memo(({
  profile, editingSection, modalTempData, handleModalInputChange,
  handleEdit, handleModalClose, handleModalSave,
  loadingSection, openSections, setOpenSections, isModalSaving, hasChanges, modalOriginalData
}) => {
  const [newSkillInput, setNewSkillInput] = useState('');
  const normalizeSkill = useCallback((skill) => {
    if (skill && typeof skill === 'object') return String(skill.id ?? skill.value ?? skill.label ?? '').trim();
    return String(skill ?? '').trim();
  }, []);

  const isDuplicateSkill = useCallback((skillValue) => {
    const normalized = String(skillValue || '').trim().toLowerCase();
    if (!normalized) return false;
    return (modalTempData?.skills || []).some(s => normalizeSkill(s).toLowerCase() === normalized);
  }, [modalTempData?.skills, normalizeSkill]);

  const handleAddSkill = useCallback(() => {
    const val = newSkillInput.trim();
    if (!val || isDuplicateSkill(val)) return;
    handleModalInputChange('skills', [...(modalTempData?.skills || []), val]);
    setNewSkillInput('');
  }, [newSkillInput, isDuplicateSkill, handleModalInputChange, modalTempData?.skills]);

  const hasSkillChanges = useMemo(() => {
    const currentSkills = (modalTempData?.skills || []).map(normalizeSkill);
    const originalSkills = (modalOriginalData?.skills || []).map(normalizeSkill);
    return JSON.stringify(currentSkills) !== JSON.stringify(originalSkills);
  }, [modalTempData?.skills, modalOriginalData?.skills, normalizeSkill]);

  return (
    <SectionCard title="Key skills" editKey="skills" onEdit={handleEdit}
      loading={loadingSection === 'skills'} isOpen={openSections['skills']}
      toggleOpen={() => setOpenSections(prev => ({ ...prev, skills: !prev['skills'] }))}>
      <div className="flex flex-wrap gap-2">
        {(profile.skills || []).map((skill, idx) => (
          <SkillBadge key={`skill_${idx}`}>
            {typeof skill === 'object' ? (skill.label || skill.value) : skill}
          </SkillBadge>
        ))}
      </div>
      <EditModal isOpen={editingSection === 'skills'} onClose={handleModalClose}
        onSave={handleModalSave} title="Edit Key Skills" isSaving={isModalSaving}
        hasChanges={editingSection === 'skills' ? hasSkillChanges : hasChanges}>
        {modalTempData && (
          <div>
            <label className="block text-xs text-gray-999 mb-1">Key skills <span className="text-red-500">*</span></label>
            <div className="flex flex-wrap max-h-96 overflow-auto gap-2">
              {(modalTempData.skills || []).map((skill, idx) => {
                const skillLabel = typeof skill === 'object' ? (skill.label || skill.value) : skill;
                return (
                  <SkillBadge key={`edit_skill_${idx}`}>
                    {skillLabel}
                    <button type="button" className="ml-1 p-0 border-none bg-transparent cursor-pointer"
                      onClick={() => { const u = [...modalTempData.skills]; u.splice(idx, 1); handleModalInputChange('skills', u); }}>
                      <FiX size={12} />
                    </button>
                  </SkillBadge>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              <input type="text" placeholder="Add a skill..." value={newSkillInput}
                onChange={e => { let v = e.target.value; if (v.length > 100) v = v.slice(0, 100); setNewSkillInput(v); }}
                disabled={isModalSaving}
                className="font-semibold text-gray-333 text-xs mt-1 p-1 border border-gray-300 rounded-md flex-1"
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSkill();
                  }
                }} />
              <button type="button"
                className="px-3 py-1 mt-1 rounded-full text-xs font-semibold profiler-bg text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                onClick={handleAddSkill}
                disabled={isModalSaving || !newSkillInput.trim() || isDuplicateSkill(newSkillInput)}>
                Add
              </button>
            </div>
            {modalTempData.skills && modalTempData.skills.length === 0 && (
              <p className="text-xs text-red-500 mt-1">Please add at least one skill.</p>
            )}
          </div>
        )}
      </EditModal>
    </SectionCard>
  );
});

// ─────────────────────────────────────────────────────────────
// CareerSection — outside ProfilerPage
// ─────────────────────────────────────────────────────────────
const CareerSection = React.memo(({
  profile, editingSection, modalTempData, handleModalInputChange,
  handleEdit, handleModalClose, handleModalSave,
  loadingSection, openSections, setOpenSections, isModalSaving, hasChanges, nationalities
}) => (
  <SectionCard title="Your career preferences" editKey="career" onEdit={handleEdit}
    loading={loadingSection === 'career'} isOpen={openSections['career']}
    toggleOpen={() => setOpenSections(prev => ({ ...prev, career: !prev['career'] }))}>
    <div className="grid scale-[0.9] sm:scale-100 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 text-sm">
      <div>
        <p className="text-xs text-gray-999 mb-1">Preferred location</p>
        <RenderTags values={parsePreferredLocationValues(profile.preferred_location)} className="text-sm" />
      </div>
      <InfoItem label="Availability to work" value={profile.availability_notice_period} isEditing={false} />
    </div>
    <EditModal isOpen={editingSection === 'career'} onClose={handleModalClose}
      onSave={handleModalSave} title="Edit Career Preferences" isSaving={isModalSaving} hasChanges={hasChanges}>
      {modalTempData && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-999 mb-1">Preferred location</label>
            <MultiSelectCandidateDropdown label="" name="preferred_location" options={nationalities.data}
              selected={modalTempData.preferred_location}
              onChange={selected => handleModalInputChange('preferred_location', selected.target?.value || [])}
              isMultiSelect isLoading={nationalities.loading} placeholder="Select country..." />
          </div>
          <InfoItem label="Availability to work" name="availability_notice_period"
            value={modalTempData.availability_notice_period} isEditing
            onChange={val => handleModalInputChange('availability_notice_period', val)} />
        </div>
      )}
    </EditModal>
  </SectionCard>
));

// ─────────────────────────────────────────────────────────────
// ProfilerPage — main component, now lean
// ─────────────────────────────────────────────────────────────
const ProfilerPage = ({ onBack, profileDetails, fullData, candidateId, userCandidateId, completionPercentage, onedit, uploadedFile }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(profileDetails);
  const [editingSection, setEditingSection] = useState(null);
  const [modalTempData, setModalTempData] = useState(null);
  const [modalOriginalData, setModalOriginalData] = useState(null);
  const [isModalSaving, setIsModalSaving] = useState(false);
  const [isConfirmSave, setIsConfirmSaving] = useState(false);
  const [modalSelectedSectorId, setModalSelectedSectorId] = useState(null);
  const {
    sectors, subsectors, technicalDisciplines, activities, projectPhases,
    languages, nationalities, dualNationalities, languageLevels,
    fetchSectors, fetchSubsectors, fetchTechnicalDisciplines,
    fetchActivities, fetchProjectPhases, fetchLanguages,
    fetchNationalities, fetchDualNationalities, fetchLanguageLevels
  } = useDropdownData();
  const [loadingSection, setLoadingSection] = useState(null);
  const [openSections, setOpenSections] = useState({
    experience: true, sectorInfo: true, career: true, header: true, skills: true, education: true, Resume: true
  });
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeUrl, setResumeUrl] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const fileInputRef2 = useRef(null);
  const [showDeclarationModal, setShowDeclarationModal] = useState(false);
  const [declarationChecked, setDeclarationChecked] = useState(false);
  const [isReviewClicked, setIsReviewClicked] = useState(false);

  useEffect(() => {
    if (!languages.loaded) fetchLanguages();
    if (!languageLevels.loaded) fetchLanguageLevels();
    if (!nationalities.loaded) fetchNationalities();
    if (!sectors.loaded) fetchSectors();
    const firstSectorId = getRawSectorIds(profile)[0];
    if (firstSectorId) { setModalSelectedSectorId(firstSectorId); fetchSubsectors(firstSectorId); }
    if (!technicalDisciplines.loaded) fetchTechnicalDisciplines();
    if (!activities.loaded) fetchActivities();
    if (!projectPhases.loaded) fetchProjectPhases();
  }, [candidateId, userCandidateId]);

  function findLanguageOption(lang, languageOptions) {
    if (!lang || !languageOptions) return null;
    if (typeof lang === 'object') {
      const id = lang.id || lang.language_code || lang.value || lang.language;
      return languageOptions.find(opt => opt.id === id || opt.language_code === id || opt.label === id || opt.value === id || opt.name === id) || null;
    }
    return languageOptions.find(opt => opt.language_code === lang || opt.id === lang || opt.value === lang || opt.label === lang || opt.name === lang) || null;
  }

  function findLevelOption(level, levelOptions) {
    if (!level || !levelOptions) return null;
    if (typeof level === 'object') {
      const id = level.id || level.level_code || level.value || level.name || level.label || level.proficiency;
      return levelOptions.find(opt => opt.id === id || opt.level_code === id || opt.value === id || opt.label === id || opt.name === id) || null;
    }
    return levelOptions.find(opt => opt.level_code === level || opt.id === level || opt.value === level || opt.name === level || opt.label === level) || null;
  }

  function getFlatLanguages(langs, otherLangs) {
    const langArr = [];
    if (Array.isArray(langs)) {
      langs.forEach(lang => {
        if (!lang) return;
        const languageOption = findLanguageOption(lang, languages.data);
        const levelOption = findLevelOption(lang.level_code || lang.level || lang.proficiency_code || lang.proficiency, languageLevels.data);
        langArr.push({
          language_id: languageOption?.id || lang.language_code || lang.code || lang.id || lang.language || lang.value || '',
          language_label: languageOption?.label || lang.language || lang.name || lang.label || lang.value || '',
          level_id: levelOption?.id || lang.level_code || lang.level || lang.proficiency_code || lang.proficiency || lang.level_id || '',
          level_label: levelOption?.label || lang.level || lang.proficiency || lang.level_name || lang.level_label || '',
          __other_language: !!lang.__other_language,
        });
      });
    }
    if (Array.isArray(otherLangs)) {
      otherLangs.forEach(ol => {
        if (!ol) return;
        let language_id, language_label, level_id, level_label;
        if (typeof ol === 'string') {
          const opt = findLanguageOption(ol, languages.data);
          language_id = opt?.id || ol; language_label = opt?.label || ol; level_id = ''; level_label = '';
        } else {
          const opt = findLanguageOption(ol, languages.data);
          language_id = opt?.id || ol.language_code || ol.code || ol.id || ol.language || ol.value || '';
          language_label = opt?.label || ol.language || ol.name || ol.label || ol.value || language_id;
          const lopt = findLevelOption(ol.level_code || ol.level || ol.proficiency_code || ol.proficiency, languageLevels.data);
          level_id = lopt?.id || ol.level_code || ol.level || ol.proficiency_code || ol.proficiency || '';
          level_label = lopt?.label || ol.level || ol.proficiency || ol.level_name || ol.level_label || level_id;
        }
        langArr.push({ language_id, language_label, level_id, level_label, __other_language: true });
      });
    }
    return langArr;
  }

  function splitFlatLanguages(languagesArray) {
    const langs = [], other_languages = [];
    (languagesArray || []).forEach(item => {
      if (item && item.__other_language) {
        other_languages.push({ language_code: item.language_label || '', level_code: item.level_label || item.level_code || item.level || '' });
      } else if (item) {
        langs.push({ language_code: item.language_id || item.language_code || item.code || item.id || item.language || item.value || '', level_code: item.level_id || item.level_code || item.level || '' });
      }
    });
    return { languages: langs, other_languages };
  }

  function getCombinedLanguageList(profileData) {
    return getFlatLanguages(profileData.languages, profileData.other_languages);
  }

  function getRawSectorIds(profileData) {
    if (Array.isArray(profileData?.sector_id)) return profileData.sector_id;
    return profileData?.sector_id ? [profileData.sector_id] : [];
  }

  function getProfileSectorOptions(profileData = profile) {
    const sectorValues = Array.isArray(profileData?.sectors) && profileData.sectors.length
      ? profileData.sectors
      : profileData?.sector_name;
    const sectorOptions = resolveToOptionObjArray(toLabelArray(sectorValues), sectors.data);
    if (sectorOptions.length) return sectorOptions;
    return resolveToOptionObjArray(getRawSectorIds(profileData), sectors.data);
  }

  function getProfileSectorIds(profileData = profile) {
    const rawSectorIds = getRawSectorIds(profileData).filter(Boolean);
    if (rawSectorIds.length) return rawSectorIds;
    if (!sectors.data.length) return [];
    return getProfileSectorOptions(profileData).map(s => s?.id ?? s?.value).filter(Boolean);
  }

  const getSectionDataForModal = useCallback((sectionKey) => {
    switch (sectionKey) {
      case 'header': return {
        first_name: profile.first_name || null, last_name: profile.last_name || null,
        location: resolveToOptionObj(profile.location, nationalities.data) || null,
        phone: profile.phone || '', date_of_birth: profile.date_of_birth || '',
        dob_disclosed: profile.dob_disclosed,
        nationality: profile.nationality === 'do not disclose' ? profile.nationality : resolveToOptionObj(profile.nationality, nationalities.data) || null,
        nationality_disclosed: profile.nationality_disclosed,
        dual_nationality: profile.dual_nationality === 'NA' ? profile.dual_nationality : resolveToOptionObjArray(profile.dual_nationality, nationalities.data),
        dual_nationality_disclosed: profile.dual_nationality_disclosed,
        languages: getFlatLanguages(profile.languages, profile.other_languages),
        gender: profile.gender || undefined,
      };
      case 'sectorInfo': return {
        sector_id: profile.sector_id || null,
        sector_name: getProfileSectorOptions(profile),
        sub_sectors: resolveToOptionObjArray(profile.sub_sectors, subsectors.data),
        discipline_names: resolveToOptionObjArray(profile.discipline_names, technicalDisciplines.data),
        activities: resolveToOptionObjArray(profile.activities, activities.data),
        project_phase: resolveToOptionObjArray(profile.project_phase, projectPhases.data),
      };
      case 'career': return {
        preferred_location: resolveToOptionObjArray(parsePreferredLocationValues(profile.preferred_location), nationalities.data) || null,
        availability_notice_period: profile.availability_notice_period || ''
      };
      case 'skills': return { skills: resolveToOptionObjArray(profile.skills, mockDropdownOptions.keySkills) };
      case 'education': return { education_details: (profile.education_details || []).map(edu => typeof edu === 'string' ? parseEducationString(edu) : deepClone(edu)) };
      case 'experience': return { experience_intervals: (profile.experience_intervals || []).map(exp => deepClone(exp)) };
      default: return {};
    }
  }, [profile, nationalities.data, sectors.data, subsectors.data, technicalDisciplines.data, activities.data, projectPhases.data, languages.data, languageLevels.data]);

  const handleEdit = useCallback(async (sectionKey) => {
    setLoadingSection(sectionKey);
    try {
      if (sectionKey === 'sectorInfo') {
        if (!sectors.loaded) await fetchSectors();
        const firstSectorId = getProfileSectorIds(profile)[0];
        if (firstSectorId) { setModalSelectedSectorId(firstSectorId); await fetchSubsectors(firstSectorId); }
        if (!technicalDisciplines.loaded) await fetchTechnicalDisciplines();
        if (!activities.loaded) await fetchActivities();
        if (!projectPhases.loaded) await fetchProjectPhases();
      } else if (sectionKey === 'career') {
        if (!nationalities.loaded) await fetchNationalities();
      } else if (sectionKey === 'header') {
        if (!languages.loaded) await fetchLanguages();
        if (!languageLevels.loaded) await fetchLanguageLevels();
        if (!nationalities.loaded) await fetchNationalities();
      }
      const data = getSectionDataForModal(sectionKey);
      setModalOriginalData(deepClone(data));
      setModalTempData(deepClone(data));
      setEditingSection(sectionKey);
    } finally { setLoadingSection(null); }
  }, [getSectionDataForModal, sectors, technicalDisciplines, activities, projectPhases, nationalities, languages, languageLevels, profile.sector_id]);

  const hasChanges = useMemo(() => {
    if (!modalTempData || !modalOriginalData) return false;
    return JSON.stringify(modalTempData) !== JSON.stringify(modalOriginalData);
  }, [modalTempData, modalOriginalData]);

  const handleModalInputChange = useCallback((field, value) => {
    setModalTempData(prev => {
      if (prev[field] === value) return prev;
      return { ...prev, [field]: value };
    });
  }, []);

  const handleModalRemoveArrayItem = useCallback((sectionKey, index) => {
    setModalTempData(prev => {
      const arr = [...(prev[sectionKey] || [])];
      arr.splice(index, 1);
      return { ...prev, [sectionKey]: arr };
    });
  }, []);

  const normalizeDate = (date) => {
    if (!date) return '';
    if (/^\d{4}-\d{2}$/.test(date)) return `${date}-01`;
    return date;
  };

  const transformModalDataForAPI = (sectionKey, data) => {
    const original = modalOriginalData;
    switch (sectionKey) {
      case 'header': {
        const headerData = {};
        const getLabel = (opt) => (typeof opt === 'object' ? (opt?.label ?? opt?.name ?? opt?.value ?? opt) : opt);
        if (data.first_name !== original?.first_name) headerData.first_name = data.first_name;
        if (data.last_name !== original?.last_name) headerData.last_name = data.last_name;
        if (getLabel(data.location) !== getLabel(original?.location)) headerData.location = getLabel(data.location);
        if (data.phone !== original?.phone) headerData.phone = data.phone;
        const dateCurrent = data.dob_disclosed ? 'NA' : data.date_of_birth;
        const dateOriginal = original?.dob_disclosed ? 'NA' : original?.date_of_birth;
        if (dateCurrent !== dateOriginal) { headerData.date_of_birth = dateCurrent === 'NA' ? null : dateCurrent; headerData.dob_disclosed = data.dob_disclosed; }
        if (data.nationality_disclosed !== original?.nationality_disclosed) headerData.nationality_disclosed = data.nationality_disclosed;
        if (getLabel(data.nationality) !== getLabel(original?.nationality)) headerData.nationality = getLabel(data.nationality);
        const dualCurrent = data.dual_nationality === 'NA' ? data.dual_nationality : Array.isArray(data.dual_nationality) ? data.dual_nationality.map(d => getLabel(d)) : [];
        const dualOriginal = original?.dual_nationality === 'NA' ? original?.dual_nationality : Array.isArray(original?.dual_nationality) ? original.dual_nationality.map(d => getLabel(d)) : [];
        if (JSON.stringify(dualCurrent) !== JSON.stringify(dualOriginal)) headerData.dual_nationality = dualCurrent;
        if (data.dual_nationality_disclosed !== original?.dual_nationality_disclosed) headerData.dual_nationality_disclosed = data.dual_nationality_disclosed;
        const { languages: currLangs, other_languages: currOthers } = splitFlatLanguages(data.languages);
        const { languages: origLangs, other_languages: origOthers } = splitFlatLanguages(original?.languages);
        if (JSON.stringify(currLangs) !== JSON.stringify(origLangs)) {
          headerData.languages = currLangs.map(lang => ({ language_code: String(lang.language_code || lang.language_id || ''), level_code: String(lang.level_code || lang.level_id || '') }));
        }
        if (JSON.stringify(currOthers) !== JSON.stringify(origOthers)) {
          headerData.other_languages = currOthers.map(lang => ({ language_code: String(lang.language_label || lang.language_code || ''), level_code: String(lang.level_label || lang.level_code || '') }));
        }
        if ((data.gender?.value ?? data.gender) !== (original?.gender?.value ?? original?.gender)) headerData.gender = data.gender?.value ?? data.gender;
        return headerData;
      }
      case 'sectorInfo': {
        const sectorData = {};
        const getIdIntArray = arr => Array.isArray(arr) ? arr.map(d => typeof d === 'object' && d !== null && d.id !== undefined ? parseInt(d.id, 10) : parseInt(d, 10)).filter(v => !isNaN(v)) : [];
        const currSectorNames = Array.isArray(data.sector_name) ? data.sector_name.map(s => s?.label || s?.name || s?.value || s) : [];
        const origSectorNames = Array.isArray(original?.sector_name) ? original.sector_name.map(s => s?.label || s?.name || s?.value || s) : [];
        if (JSON.stringify(currSectorNames) !== JSON.stringify(origSectorNames)) sectorData.sectors = currSectorNames;
        const currSubs = getIdIntArray(data.sub_sectors); const origSubs = getIdIntArray(original?.sub_sectors);
        if (JSON.stringify(currSubs) !== JSON.stringify(origSubs)) sectorData.sub_sectors = currSubs;
        const currDisc = getIdIntArray(data.discipline_names); const origDisc = getIdIntArray(original?.discipline_names);
        if (JSON.stringify(currDisc) !== JSON.stringify(origDisc)) sectorData.disciplines = currDisc;
        const currProj = getIdIntArray(data.project_phase); const origProj = getIdIntArray(original?.project_phase);
        if (JSON.stringify(currProj) !== JSON.stringify(origProj)) sectorData.project_phase = currProj;
        const currAct = getIdIntArray(data.activities); const origAct = getIdIntArray(original?.activities);
        if (JSON.stringify(currAct) !== JSON.stringify(origAct)) sectorData.activities = currAct;
        return sectorData;
      }
      case 'career': {
        const careerData = {};
        const getPreferredLocationNames = (locations) => Array.isArray(locations)
          ? locations.map(loc => (loc && typeof loc === 'object') ? (loc.label ?? loc.name ?? loc.value ?? loc.id) : loc)
            .map(loc => String(loc || '').trim())
            .filter(Boolean)
          : [];
        const currentPreferredLocations = getPreferredLocationNames(data.preferred_location);
        const originalPreferredLocations = getPreferredLocationNames(original?.preferred_location);
        const currentPreferredLocationStr = currentPreferredLocations.join(', ');
        const originalPreferredLocationStr = originalPreferredLocations.join(', ');
        if (currentPreferredLocationStr !== originalPreferredLocationStr) careerData.preferred_location = currentPreferredLocationStr;
        if (data.availability_notice_period !== original?.availability_notice_period) careerData.availability_notice_period = data.availability_notice_period;
        return careerData;
      }
      case 'skills': {
        const currentSkills = (data.skills || []).map(s => (s && typeof s === 'object' && s.id !== undefined) ? s.id : s);
        const originalSkills = (original.skills || []).map(s => (s && typeof s === 'object' && s.id !== undefined) ? s.id : s);
        if (JSON.stringify(currentSkills) !== JSON.stringify(originalSkills)) return { skills: currentSkills };
        return {};
      }
      case 'education': {
        const currentEducation = (data.education_details || []).map(edu => ({ degree: edu.degree || '', major: edu.field || edu.area_of_study || '', field_of_study: edu.field || edu.field_of_study || '', institution: edu.institution || '', year_of_completion: edu.duration_text || edu.year_of_completion || '', year_of_study: edu.duration_total || edu.year_of_study || '' }));
        const originalEducation = (original.education_details || []).map(edu => ({ degree: edu.degree || '', major: edu.field || edu.area_of_study || '', field_of_study: edu.field || edu.field_of_study || '', institution: edu.institution || '', year_of_completion: edu.duration_text || edu.year_of_completion || '', year_of_study: edu.duration_total || edu.year_of_study || '' }));
        if (JSON.stringify(currentEducation) !== JSON.stringify(originalEducation)) return { education_details: currentEducation };
        return {};
      }
      case 'experience': {
        const currentExperience = (data.experience_intervals || []).map(exp => ({ title: exp.title || '', company: exp.company || '', start_date: normalizeDate(exp.start_date) || '', end_date: normalizeDate(exp.end_date) || '', duration_text: exp.duration_text || '', currentlyWorking: exp.currentlyWorking || false }));
        const originalExperience = (original.experience_intervals || []).map(exp => ({ title: exp.title || '', company: exp.company || '', start_date: exp.start_date || '', end_date: exp.end_date || '', duration_text: exp.duration_text || '', currentlyWorking: exp.currentlyWorking || false }));
        if (JSON.stringify(currentExperience) !== JSON.stringify(originalExperience)) return { experience_intervals: currentExperience };
        return {};
      }
      default: return {};
    }
  };

  const handleModalSave = useCallback(async () => {
    if (!hasChanges) { toast.info('No changes to save.'); return; }
    if (!validateSectionData(editingSection, modalTempData)) { toast.warning(missingFieldsMessage(editingSection)); return; }
    setIsModalSaving(true);
    try {
      const modifications = transformModalDataForAPI(editingSection, modalTempData);
      if (Object.keys(modifications).length === 0) { toast.info('No effective changes to save.'); setIsModalSaving(false); return; }
      const requestBody = { user_payload: { request_id: generateRequestId() }, candidate_id: candidateId, resume_modifications: modifications, edited_fields: Object.keys(modifications) };
      const response = await resumeUpload.saveResumeModifications(requestBody);
      if (response.success) {
        onedit(candidateId);
        setProfile(prev => {
          const u = { ...prev };
          if (editingSection === 'header') {
            const { languages: splitLangs, other_languages: splitOthers } = splitFlatLanguages(modalTempData.languages);
            Object.assign(u, { first_name: modalTempData.first_name, last_name: modalTempData.last_name, location: modalTempData.location?.label || modalTempData.location, phone: modalTempData.phone, date_of_birth: modalTempData.dob_disclosed ? 'NA' : modalTempData.date_of_birth, dob_disclosed: modalTempData.dob_disclosed, nationality: modalTempData.nationality?.label || modalTempData.nationality, nationality_disclosed: modalTempData.nationality_disclosed, dual_nationality_disclosed: modalTempData.dual_nationality_disclosed, dual_nationality: modalTempData.dual_nationality === 'NA' ? modalTempData.dual_nationality : modalTempData.dual_nationality?.map(d => d.label || d) || [], languages: splitLangs, other_languages: splitOthers, gender: modalTempData.gender });
          } else if (editingSection === 'sectorInfo') {
            const selectedSectors = Array.isArray(modalTempData.sector_name) ? modalTempData.sector_name : [];
            Object.assign(u, { sector_id: selectedSectors.map(s => s.id ?? s.value).filter(Boolean), sectors: selectedSectors.map(s => s.label || s.name || s.value || s).filter(Boolean), sector_name: selectedSectors, sub_sectors: modalTempData.sub_sectors, discipline_names: modalTempData.discipline_names, activities: modalTempData.activities, project_phase: modalTempData.project_phase, project_phase_names: Array.isArray(modalTempData.project_phase) ? modalTempData.project_phase.map(p => p.label) : [], activity_names: Array.isArray(modalTempData.activities) ? modalTempData.activities.map(a => a.label) : [], sub_sector_names: Array.isArray(modalTempData.sub_sectors) ? modalTempData.sub_sectors.map(s => s.label) : [] });
          } else if (editingSection === 'career') {
            Object.assign(u, { preferred_location: modalTempData.preferred_location, availability_notice_period: modalTempData.availability_notice_period });
          } else if (editingSection === 'skills') {
            Object.assign(u, { skills: modalTempData.skills });
          } else if (editingSection === 'education') {
            Object.assign(u, { education_details: modalTempData.education_details });
          } else if (editingSection === 'experience') {
            let totalMonths = 0;
            (modalTempData.experience_intervals || []).forEach(exp => {
              const s = new Date(exp.start_date || exp.startDate), e = exp.currentlyWorking ? new Date() : new Date(exp.end_date || exp.endDate);
              if (s && e && !isNaN(s) && !isNaN(e)) totalMonths += Math.max(0, (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()));
            });
            const yrs = Math.floor(totalMonths / 12), mos = totalMonths % 12;
            const yearsOfExperience = (yrs === 0 && mos === 0) ? null : yrs === 0 ? `${mos} ${mos === 1 ? 'Month' : 'Months'}` : mos === 0 ? `${yrs} ${yrs === 1 ? 'Year' : 'Years'}` : `${yrs} ${yrs === 1 ? 'Year' : 'Years'} ${mos} ${mos === 1 ? 'Month' : 'Months'}`;
            Object.assign(u, { experience_intervals: modalTempData.experience_intervals, years_of_experience: yearsOfExperience });
          }
          return u;
        });
        handleModalClose();
        toast.success('Profile updated successfully!');
      } else throw new Error(response.message || 'Failed to update profile');
    } catch (error) {
      toast.error(error.message || 'Failed to save changes. Please try again.');
    } finally { setIsModalSaving(false); }
  }, [hasChanges, editingSection, modalTempData, candidateId, onedit, toast]);

  const handleModalClose = useCallback(() => {
    setEditingSection(null); setModalTempData(null); setModalOriginalData(null); setModalSelectedSectorId(null);
    profileDetails.is_profile_verified = true;
  }, [profileDetails]);

  const verifyProfile = useCallback(async () => {
    try {
      setIsConfirmSaving(true);
      const res = await resumeUpload.verifyProfile({ user_payload: { request_id: generateRequestId() }, candidate_id: profile.id });
      if (res.success) { profileDetails.is_profile_verified = true; setIsReviewClicked(false); }
    } catch { toast.info('Failed to verify the profile.'); }
  }, [profile.id, profileDetails, toast]);

  const genderOptions = [
    { value: 'Female', label: 'Female', id: 'female' },
    { value: 'Male', label: 'Male', id: 'male' },
  ];

  // ── ProfileHeaderSection stays inline (no array state = no focus issue) ──
  const isHeaderOpen = editingSection === 'header';
  const isSectorOpen = editingSection === 'sectorInfo';
  const allLanguagesForView = getCombinedLanguageList(profile);

 const handleModalSectorInfoChange = useCallback((field, selectedValue) => {
  let value = selectedValue;
  if (selectedValue?.target?.value !== undefined) value = selectedValue.target.value;

  switch (field) {
    case 'sector_name': {
      const sectorObjs = Array.isArray(value) ? value : value ? [value] : [];
      const sectorIds = sectorObjs.map(s => s?.id ?? s?.value).filter(Boolean);

      // Batch all sector-related updates in ONE setState call
      setModalTempData(prev => ({
        ...prev,
        sector_name: sectorObjs,
        sector_id: sectorIds,
        sub_sectors: [],
      }));

      setModalSelectedSectorId(sectorIds[0] || null);
      if (sectorIds[0]) fetchSubsectors(sectorIds[0]);
      break;
    }
    case 'sub_sectors':
      setModalTempData(prev => ({
        ...prev,
        sub_sectors: value
          ? (Array.isArray(value) ? value.map(v => resolveToOptionObj(v, subsectors.data)) : [resolveToOptionObj(value, subsectors.data)])
          : [],
      }));
      break;
    case 'discipline_names':
      setModalTempData(prev => ({
        ...prev,
        discipline_names: value
          ? (Array.isArray(value) ? value.map(v => resolveToOptionObj(v, technicalDisciplines.data)) : [resolveToOptionObj(value, technicalDisciplines.data)])
          : [],
      }));
      break;
    case 'activities':
      setModalTempData(prev => ({
        ...prev,
        activities: value
          ? (Array.isArray(value) ? value.map(v => resolveToOptionObj(v, activities.data)) : [resolveToOptionObj(value, activities.data)])
          : [],
      }));
      break;
    case 'project_phase':
      setModalTempData(prev => ({
        ...prev,
        project_phase: value
          ? (Array.isArray(value) ? value.map(v => resolveToOptionObj(v, projectPhases.data)) : [resolveToOptionObj(value, projectPhases.data)])
          : [],
      }));
      break;
    default:
      break;
  }
}, [fetchSubsectors, subsectors.data, technicalDisciplines.data, activities.data, projectPhases.data]);
  const handleEditSector = useCallback(async () => {
    setLoadingSection('sectorInfo');
    try {
      if (!sectors.loaded) await fetchSectors();
      if (!technicalDisciplines.loaded) await fetchTechnicalDisciplines();
      if (!activities.loaded) await fetchActivities();
      if (!projectPhases.loaded) await fetchProjectPhases();
      const existingSectorIds = getProfileSectorIds(profile);
      const firstSectorId = existingSectorIds[0] || null;
      if (firstSectorId) { setModalSelectedSectorId(firstSectorId); await fetchSubsectors(firstSectorId); }
      const sectorModalData = {
        sector_id: profile.sector_id || null,
        sector_name: getProfileSectorOptions(profile),
        sub_sectors: resolveToOptionObjArray(profile.sub_sectors, subsectors.data),
        discipline_names: resolveToOptionObjArray(profile.discipline_names, technicalDisciplines.data),
        activities: resolveToOptionObjArray(profile.activities, activities.data),
        project_phase: resolveToOptionObjArray(profile.project_phase_names, projectPhases.data, 'project_phase_name'),
      };
      setModalOriginalData(deepClone(sectorModalData));
      setModalTempData(deepClone(sectorModalData));
      setEditingSection('sectorInfo');
    } finally { setLoadingSection(null); }
  }, [profile, sectors, subsectors, technicalDisciplines, activities, projectPhases, fetchSectors, fetchSubsectors, fetchTechnicalDisciplines, fetchActivities, fetchProjectPhases]);

  // Resume section helpers
  const handleResumeDownload = async () => {
    if (!profile?.file_path) return;
    try {
      setShowResumeModal(true); setResumeLoading(true);
      const res = await RecruiterApi.getResumePreview({ user_payload: { request_id: generateRequestId() }, candidate_id: profile.id });
      if (res.success && res.data?.sas_token_url) { setResumeUrl(res.data.sas_token_url); setResumeLoading(false); }
    } catch { toast.info('Failed to download resume. Please try again later or contact support.'); }
  };

  const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  const maxSize = 5 * 1024 * 1024;
  const isAllowedExtension = (fileName) => ['pdf', 'doc', 'docx'].includes(fileName.split('.').pop().toLowerCase());
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || e.target.files.length > 1) return;
    if (!allowedTypes.includes(file.type) && !isAllowedExtension(file.name)) return;
    if (file.size > maxSize) return;
    uploadedFile(file);
  };
  const formatUploadedDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date)) return '';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  function toLabelArray(val) {
    if (val == null) return [];
    if (Array.isArray(val)) return val.map(v => extractLabels(v)).filter(Boolean);
    const label = extractLabels(val);
    return label ? [label] : [];
  }
  const getGender = (gender) => {
    if (!gender) return null;
    if (typeof gender === 'string') return gender.toLowerCase() === 'male' ? 'Male' : 'Female';
    return gender.label?.toLowerCase() === 'male' ? 'Male' : 'Female';
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      <div className="flex-shrink-0 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <button onClick={() => { if (!profileDetails.is_profile_verified || isReviewClicked) setShowDeclarationModal(true); else onBack(); }}
          className="mph-primary group rounded-full px-2 pl-1 py-1 bg-gray-100 flex items-center text-sm font-medium">
          <div className='flex rounded-full bg-white p-1 mr-2 transition-transform duration-300 ease-in-out group-hover:translate-x-[5px]'>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          </div>
          <div className='text-[0.7rem] font-semibold'>See All Jobs</div>
        </button>
      </div>

      <div className="flex-1 pr-0 sm:pr-2 pb-20">
        {/* ── Personal Information (header) ── */}
        <SectionCard title="Personal Information" editKey="header" onEdit={handleEdit}
          loading={loadingSection === 'header'} isOpen={openSections['header']}
          toggleOpen={() => setOpenSections(prev => ({ ...prev, header: !prev['header'] }))}>
          <div className="flex flex-col md:flex-row items-start h-full md:gap-6 w-full">
            <div className="flex m-auto items-center justify-center md:items-start md:justify-start px-6 gap-4 md:gap-6 w-full md:w-auto scale-[0.6] sm:scale-100">
              <RadialProgressAvatar size="150" completion={completionPercentage} firstName={profile.first_name} lastName={profile.last_name} />
            </div>
            <div className="flex gap-2 w-full flex-col">
              <div className="flex flex-col scale-[0.8] md:scale-100 pb-3 items-center justify-center md:items-start md:justify-start border-b">
                <h2 className="text-sm text-gray-333 font-bold flex gap-2">{profile.first_name} {profile.last_name}<FiCheckCircle className="mph-primary" size={16} /></h2>
                <p className="text-gray-333 text-sm break-words" style={{ wordBreak: 'break-word', maxWidth: 250 }}>{profile.highest_position || 'Position not specified'}</p>
                <p className="text-xs text-gray-999">{profile.email}</p>
              </div>
              <div className="flex-1 grid grid-cols-2 mt-0 border-t-0 pt-0 sm:mt-2 sm:border-t sm:pt-4 md:mt-0 md:border-t-0 md:pt-0 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 text-sm border-gray-300 scale-[0.8] sm:scale-100">
                <InfoItem label="Current Location" value={profile.location} isRequired isEditing={false} />
                <InfoItem label="Phone number" value={profile.phone} isRequired isEditing={false} />
                <InfoItem label="Gender" value={getGender(profile.gender)} isRequired isEditing={false} />
                <div>
                  <p className="text-xs text-gray-999 mb-1">Languages <span className="text-red-500">*</span></p>
                  <LanguageSelector languages={allLanguagesForView} isEditing={false} options={languages.data} languageLevels={languageLevels.data} isLanguageLevelLoading={languageLevels.loading} />
                </div>
                <div>
                  <p className="text-xs text-gray-999 mb-1">Nationality <span className="text-red-500">*</span></p>
                  {profile.nationality_disclosed ? <span className="italic text-xs text-gray-500">Not disclosed</span> : <RenderTags values={profile.nationality} className="text-sm" />}
                </div>
                <div>
                  <p className="text-xs text-gray-999 mb-1">Dual Nationality</p>
                  {profile.dual_nationality_disclosed ? <span className="text-xs italic text-gray-500">Not disclosed</span> : <RenderTags values={profile.dual_nationality} className="text-sm" />}
                </div>
                <div>
                  <p className="text-xs text-gray-999 mb-1">Date of Birth <span className="text-red-500">*</span></p>
                  <span className="font-semibold text-gray-333 text-xs mt-1">
                    {profile.dob_disclosed ? <span className="italic text-gray-500">Not disclosed</span> : profile.date_of_birth || 'Nil'}
                  </span>
                </div>
              </div>
            </div>
            {!fullData?.validation?.all_present && (
              <div className="h-full relative flex flex-col gap-2 rounded-2xl cborder p-2 w-1/2 h-[15rem]">
                <div className="absolute top-[-11px] bg-red-500 text-white px-2 py-1 rounded text-[9px] font-semibold">Add missing details</div>
                <div className="w-full h-full flex flex-col gap-2 p-2 overflow-auto">
                  {fullData?.validation?.empty_fields?.map((item, index) => (
                    <div key={index} title={`Add ${item}`} className="w-full flex rounded gap-2 bg-[#FFF2E3] p-1 items-center">
                      <div className="p-1 cursor-pointer rounded-full bg-white"><svg className="w-5" viewBox="0 0 24 24" fill="none"><path d="M12.01 19V9M12.01 5H12" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                      <div className="w-full text-[14px] font-bold text-gray-500 flex">{item}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sector display + edit */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-gray-700">Sector Information</p>
              <button onClick={handleEditSector} disabled={loadingSection === 'sectorInfo'}
                className="text-xs mph-primary flex items-center gap-1 hover:opacity-70 disabled:opacity-40">
                {loadingSection === 'sectorInfo' ? <span className="animate-spin inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full" /> : <FiEdit size={13} />}
              </button>
            </div>
            <div className="grid scale-[0.9] sm:scale-100 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 text-sm">
              <div><p className="text-xs text-gray-999 mb-1">Sector<span className="text-red-500">*</span></p><CollapsibleTags values={toLabelArray(profile.sectors?.length ? profile.sectors : profile.sector_name)} limit={1} /></div>
              <div><p className="text-xs text-gray-999 mb-1">Subsector</p><CollapsibleTags values={toLabelArray(profile.sub_sector_names)} limit={1} /></div>
              <div><p className="text-xs text-gray-999 mb-1">Technical Disciplines</p><CollapsibleTags values={toLabelArray(profile.discipline_names)} limit={1} /></div>
              <div><p className="text-xs text-gray-999 mb-1">Activities</p><CollapsibleTags values={toLabelArray(profile.activity_names)} limit={1} /></div>
              <div><p className="text-xs text-gray-999 mb-1">Project Phases<span className="text-red-500">*</span></p><CollapsibleTags values={toLabelArray(profile.project_phase_names)} limit={1} /></div>
            </div>
          </div>

          {/* Header edit modal */}
          <EditModal isOpen={isHeaderOpen} onClose={handleModalClose} onSave={handleModalSave} title="Edit Personal Information" isSaving={isModalSaving} hasChanges={hasChanges}>
            {modalTempData && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoItem label="First Name" name="first_name" value={modalTempData.first_name} isEditing isRequired onChange={val => handleModalInputChange('first_name', val)} />
                <InfoItem label="Last Name" name="last_name" value={modalTempData.last_name} isEditing isRequired onChange={val => handleModalInputChange('last_name', val)} />
                <InfoItem label="Current Location" name="location" value={modalTempData.location} isEditing isRequired options={nationalities.data} type="select" onChange={val => handleModalInputChange('location', val)} />
                <InfoItem label="Phone number" name="phone" value={modalTempData.phone} isEditing isRequired onChange={val => handleModalInputChange('phone', val)} />
                <InfoItem label="Gender" name="gender" value={modalTempData.gender} isEditing isRequired type="select" options={genderOptions} onChange={val => handleModalInputChange('gender', val)} />
                <div>
                  <label className="block text-xs text-gray-999 mb-1">Languages <span className="text-red-500">*</span></label>
                  <LanguageSelector languages={modalTempData.languages} onChange={sel => handleModalInputChange('languages', sel)} options={languages.data} isLoading={languages.loading} isEditing languageLevels={languageLevels.data} isLanguageLevelLoading={languageLevels.loading} />
                </div>
                <div>
                  <InfoItem label="Nationality" name="nationality" isRequired value={modalTempData.nationality} isEditing onChange={val => handleModalInputChange('nationality', val)} options={nationalities.data} type="select" isDisabled={modalTempData.nationality_disclosed} />
                  <label className="mt-2 flex items-center gap-1 text-xs text-gray-600">
                    <input type="checkbox" checked={modalTempData.nationality_disclosed} onChange={e => setModalTempData(prev => ({ ...prev, nationality_disclosed: e.target.checked }))} /> Do not wish to disclose
                  </label>
                </div>
                <div>
                  <InfoItem label="Dual Nationality" name="dual_nationality" value={modalTempData.dual_nationality} isEditing onChange={val => handleModalInputChange('dual_nationality', val)} options={nationalities.data} type="select" isDisabled={modalTempData.dual_nationality_disclosed} />
                  <label className="mt-2 flex items-center gap-1 text-xs text-gray-600">
                    <input type="checkbox" checked={modalTempData.dual_nationality_disclosed} onChange={e => setModalTempData(prev => ({ ...prev, dual_nationality_disclosed: e.target.checked }))} /> Do not wish to disclose
                  </label>
                </div>
                <div>
                  <label className="block text-xs text-gray-999 mb-1">Date of Birth <span className="text-red-500">*</span></label>
                  <div className="flex flex-col gap-2">
                    <InfoItem label="" name="date_of_birth" value={modalTempData.date_of_birth || ''} isEditing type="date" disabled={modalTempData.dob_disclosed} max={new Date().toISOString().split('T')[0]} onChange={val => handleModalInputChange('date_of_birth', val)} />
                    <label className="flex items-center gap-1 text-xs text-gray-600">
                      <input type="checkbox" checked={modalTempData.dob_disclosed} onChange={e => { const c = e.target.checked; setModalTempData(prev => ({ ...prev, dob_disclosed: c, date_of_birth: c ? null : profile.date_of_birth })); }} /> Do not wish to disclose
                    </label>
                  </div>
                </div>
              </div>
            )}
          </EditModal>

          {/* Sector edit modal */}
          <EditModal isOpen={isSectorOpen} onClose={handleModalClose} onSave={handleModalSave} title="Edit Sector Information" isSaving={isModalSaving} hasChanges={hasChanges}>
            {modalTempData && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-xs text-gray-999 mb-1">Sector <span className="text-red-500">*</span></label><MultiSelectCandidateDropdown label="" name="sector_name" options={sectors.data} selected={modalTempData.sector_name || []} onChange={sel => handleModalSectorInfoChange('sector_name', sel)} isMultiSelect isLoading={sectors.loading}  closeOnSelect={true} /></div>
                <div><label className="block text-xs text-gray-999 mb-1">Subsector</label><MultiSelectCandidateDropdown label="" name="sub_sectors" options={subsectors.data} selected={modalTempData.sub_sectors || []} onChange={sel => handleModalSectorInfoChange('sub_sectors', sel)} isMultiSelect isLoading={subsectors.loading} disabled={!modalSelectedSectorId} placeholder={!modalSelectedSectorId ? 'Select sector first' : 'Search subsector...'} /></div>
                <div><label className="block text-xs text-gray-999 mb-1">Technical Disciplines</label><MultiSelectCandidateDropdown label="" name="discipline_names" options={technicalDisciplines.data} selected={modalTempData.discipline_names || []} onChange={sel => handleModalSectorInfoChange('discipline_names', sel)} isMultiSelect isLoading={technicalDisciplines.loading} /></div>
                <div><label className="block text-xs text-gray-999 mb-1">Activities</label><MultiSelectCandidateDropdown label="" name="activities" options={activities.data} selected={modalTempData.activities || []} onChange={sel => handleModalSectorInfoChange('activities', sel)} isMultiSelect isLoading={activities.loading} /></div>
                <div><label className="block text-xs text-gray-999 mb-1">Project Phases <span className="text-red-500">*</span></label><MultiSelectCandidateDropdown label="" name="project_phase" options={projectPhases.data} selected={modalTempData.project_phase || []} onChange={sel => handleModalSectorInfoChange('project_phase', sel)} isMultiSelect isLoading={projectPhases.loading} /></div>
              </div>
            )}
          </EditModal>
        </SectionCard>

        {/* ── Sections now defined OUTSIDE ProfilerPage — no remount on state change ── */}
        <ExperienceSection
          profile={profile} editingSection={editingSection} modalTempData={modalTempData}
          setModalTempData={setModalTempData} handleEdit={handleEdit} handleModalClose={handleModalClose}
          handleModalSave={handleModalSave} handleModalRemoveArrayItem={handleModalRemoveArrayItem}
          loadingSection={loadingSection} openSections={openSections} setOpenSections={setOpenSections}
          isModalSaving={isModalSaving} hasChanges={hasChanges} toast={toast}
        />

        {userCandidateId && (
          <SectionCard title="Resume" isOpen={openSections['Resume']} toggleOpen={() => setOpenSections(prev => ({ ...prev, Resume: !prev['Resume'] }))}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-2 sm:p-4 gap-2">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-sm">{profile.file_name || 'Resume.pdf'}</p>
                {profile.file_path && (
                  <button type="button" onClick={handleResumeDownload} className="h-7 w-7 flex items-center justify-center rounded-full bg-white hover:shadow-lg">
                    <FiEye className="mph-primary text-sm" />
                  </button>
                )}
              </div>
              {showResumeModal && <ResumeModal isOpen={showResumeModal} onClose={() => setShowResumeModal(false)} title={profile.file_name || 'Resume.pdf'} resumeUrl={resumeUrl} loading={resumeLoading} />}
              <p className="text-xs text-gray-999">Uploaded on {formatUploadedDate(profile.upload_time || profile.created_at)}</p>
            </div>
            <div className="border-2 border-dashed mph-button-border rounded-xl p-4 sm:p-6 text-center mt-6">
              <input type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" ref={fileInputRef2} style={{ display: 'none' }} onChange={handleFileChange} multiple={false} />
              <button className="profiler-bg font-semibold text-xs px-3 py-1.5 rounded-full flex items-center justify-center gap-2 mx-auto text-white hover:opacity-90" onClick={() => { if (fileInputRef2.current) { fileInputRef2.current.value = null; fileInputRef2.current.click(); } }}>Update resume</button>
              <p className="text-center text-xs text-gray-400 mt-2">Supported files: <span className="font-bold text-gray-333">PDF, DOCX</span> · Max size: <span className="font-bold text-gray-333">2MB</span></p>
            </div>
          </SectionCard>
        )}

        <EducationSection
          profile={profile} editingSection={editingSection} modalTempData={modalTempData}
          setModalTempData={setModalTempData} handleEdit={handleEdit} handleModalClose={handleModalClose}
          handleModalSave={handleModalSave} loadingSection={loadingSection} openSections={openSections}
          setOpenSections={setOpenSections} isModalSaving={isModalSaving} hasChanges={hasChanges} toast={toast}
          showDeclarationModal={showDeclarationModal} declarationChecked={declarationChecked}
          setDeclarationChecked={setDeclarationChecked} setShowDeclarationModal={setShowDeclarationModal}
          setIsReviewClicked={setIsReviewClicked} verifyProfile={verifyProfile} onBack={onBack}
        />

        <SkillsSection
          profile={profile} editingSection={editingSection} modalTempData={modalTempData}
          handleModalInputChange={handleModalInputChange} handleEdit={handleEdit} handleModalClose={handleModalClose}
          handleModalSave={handleModalSave} loadingSection={loadingSection} openSections={openSections}
          setOpenSections={setOpenSections} isModalSaving={isModalSaving} hasChanges={hasChanges}
          modalOriginalData={modalOriginalData}
        />

        <CareerSection
          profile={profile} editingSection={editingSection} modalTempData={modalTempData}
          handleModalInputChange={handleModalInputChange} handleEdit={handleEdit} handleModalClose={handleModalClose}
          handleModalSave={handleModalSave} loadingSection={loadingSection} openSections={openSections}
          setOpenSections={setOpenSections} isModalSaving={isModalSaving} hasChanges={hasChanges}
          nationalities={nationalities}
        />
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white/95 backdrop-blur-sm px-4 py-2 text-center shadow-[0_-2px_10px_rgba(0,0,0,0.06)]">
          <span className="text-[10px] text-gray-500">Disclaimer: This resume was processed using AI. Please verify details for accuracy.</span>
        </div>
      </div>
    </div>
  );
};

export default ProfilerPage;
